import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import {
  Home,
  Package,
  Users,
  Settings as SettingsIcon,
  Ticket,
  MessageCircle,
  Newspaper,
  LucideProps,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { roleMenus, pickLocale } from "@/lib/roleMenus";
import type { LocaleCode } from "@/lib/roleMenus";
import { useUnreadCount } from "@/hooks/useUnreadCount";

// Primary app sidebar with localized navigation
export const PrimarySidebar: React.FC = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [headerLogoError, setHeaderLogoError] = useState(false);
  const [footerLogoError, setFooterLogoError] = useState(false);
  const { unreadCount } = useUnreadCount();

  type IconType = React.ComponentType<LucideProps>;
  const iconMap: Record<string, IconType> = {
    LayoutDashboard: Home,
    Users,
    Package,
    Settings: SettingsIcon,
    Star: Package,
    UserCircle: Users,
    FileChartColumn: Package,
    Ticket,
    MessageCircle,
    Newspaper,
  };

  const currentRole = user?.user_metadata?.role || "buyer";
  // Use current i18n language to derive locale reactively on language changes
  const locale: LocaleCode = pickLocale(
    i18n.language ||
      (typeof navigator !== "undefined" ? navigator.language : "en"),
  );
  const menuItems =
    roleMenus[currentRole as keyof typeof roleMenus]?.menu || [];

  return (
    <Sidebar
      collapsible="icon"
      variant="inset"
      className="w-[260px] bg-gray-50/95 backdrop-blur-sm border-gray-200"
    >
      <SidebarHeader>
        <div className="flex items-center px-spacing-4 py-spacing-4">
          {!headerLogoError ? (
            <img
              src="/trafomatic-logo.svg"
              alt="Trafomatic"
              className="h-10 w-10"
              style={{ objectFit: "contain" }}
              onError={() => setHeaderLogoError(true)}
            />
          ) : (
            <div className="h-10 w-10 flex items-center justify-center bg-primary text-primary-foreground rounded text-base font-bold">
              TF
            </div>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          {menuItems.map(({ route, label, icon, key }) => {
            const Icon = iconMap[icon] || Home;
            const localized = label[locale];
            const isChatsRoute = route === "/chats"; // Detect chats route
            const showBadge = isChatsRoute && unreadCount > 0; // Show badge if chats and has unread

            return (
              <SidebarMenuItem key={route}>
                <SidebarMenuButton
                  asChild
                  tooltip={localized}
                  className="font-body-medium text-body-sm hover:bg-gray-200/70 transition-colors duration-200"
                >
                  <NavLink
                    to={route}
                    className={({ isActive }) =>
                      `flex items-center gap-spacing-3 px-spacing-3 py-spacing-2 rounded-radius-sm ${
                        isActive
                          ? "bg-accent text-accent-foreground font-body-semibold"
                          : "text-gray-700"
                      }`
                    }
                  >
                    <Icon className="h-5 w-5" />
                    <span className="leading-none">{localized}</span>
                    {showBadge && (
                      <Badge
                        variant="escalated"
                        className="ml-auto h-5 min-w-5 px-1 flex items-center justify-center text-xs bg-red-500 text-white hover:bg-red-600"
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Badge>
                    )}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter>
        <div className="flex items-center gap-2 px-spacing-4 py-spacing-4 text-label-xs text-gray-500">
          {!footerLogoError ? (
            <img
              src="/trafomatic-logo.svg"
              alt="Trafomatic"
              className="h-6 w-6"
              style={{ objectFit: "contain" }}
              onError={() => setFooterLogoError(true)}
            />
          ) : (
            <div className="h-6 w-6 flex items-center justify-center bg-primary text-primary-foreground rounded text-xs font-bold">
              TF
            </div>
          )}
          <span>© {new Date().getFullYear()}</span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

// Convenience shell that provides the context wrapper
export const SidebarShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <SidebarProvider>{children}</SidebarProvider>;
};

export default PrimarySidebar;
